/**
 * Authentication context provider for managing Supabase authentication state.
 * Provides centralized auth state management across the application.
 *
 * @module AuthContext
 * @example
 * // Wrap your app with AuthProvider
 * <AuthProvider>
 *   <App />
 * </AuthProvider>
 *
 * @example
 * // Use auth in any component
 * const { user, session, isMember, isVenueOwner } = useAuth();
 */

import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "../utils/supabase";
import type { User, Session } from "@supabase/supabase-js";

/**
 * Shape of the authentication context.
 */
interface AuthContextType {
  /** Currently authenticated user, or null if not authenticated */
  user: User | null;
  /** Current session with tokens and metadata, or null if not authenticated */
  session: Session | null;
  /** Whether auth state is still being determined on initial load */
  isLoading: boolean;
  /** User type from metadata ('member' | 'venue_owner' | null) */
  userType: string | null;
  /** True if user has 'member' type in metadata */
  isMember: boolean;
  /** True if user has 'venue_owner' type in metadata */
  isVenueOwner: boolean;
  /** True if user is authenticated (has a session) */
  isAuthenticated: boolean;
}

/**
 * Authentication context - must be accessed via useAuth() hook.
 * @private
 */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Props for AuthProvider component.
 */
interface AuthProviderProps {
  /** Child components that will have access to auth context */
  children: React.ReactNode;
}

/**
 * Authentication provider component that manages global auth state.
 * Subscribes to Supabase auth changes and provides user/session data to all child components.
 *
 * Features:
 * - Automatic session initialization on mount
 * - Real-time auth state updates via onAuthStateChange
 * - Loading state for initial auth check
 * - Automatic cleanup of subscriptions
 * - Helper computed values (isMember, isVenueOwner, etc.)
 *
 * @param props - Component props
 * @returns Provider component wrapping children with auth context
 *
 * @example
 * // In  App.tsx
 * <AuthProvider>
 *   <BrowserRouter>
 *     <App />
 *   </BrowserRouter>
 * </AuthProvider>
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    /**
     * Initialize auth state by fetching current session.
     * This runs once on mount to restore auth state from stored tokens.
     */
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    /**
     * Subscribe to auth state changes (sign in, sign out, token refresh).
     * Updates user and session state whenever auth state changes.
     */
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    // Cleanup subscription on unmount to prevent memory leaks
    return () => subscription.unsubscribe();
  }, []);

  // Compute derived auth values for convenience
  const userType = (session?.user?.user_metadata?.user_type as string) ?? null;
  const isMember = userType === "member";
  const isVenueOwner = userType === "venue_owner";
  const isAuthenticated = !!session;

  const value: AuthContextType = {
    user,
    session,
    isLoading,
    userType,
    isMember,
    isVenueOwner,
    isAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access authentication context.
 * Provides current user, session, loading state, and helper computed values.
 *
 * Must be used within a component wrapped by AuthProvider, otherwise throws an error.
 *
 * @returns Authentication context with user data and helper values
 * @throws {Error} If used outside of AuthProvider
 *
 * @example
 * // Basic usage - check if user is signed in
 * const { user, isLoading } = useAuth();
 *
 * if (isLoading) return <Spinner />;
 * if (!user) return <SignInPrompt />;
 *
 * @example
 * // Access user metadata
 * const { session, isMember, isVenueOwner } = useAuth();
 * const username = session?.user?.user_metadata?.username;
 *
 * @example
 * // Conditional navigation based on user type
 * const { user, isMember, isVenueOwner } = useAuth();
 *
 * const handleClose = () => {
 *   if (!user) navigate("/");
 *   else if (isMember) navigate("/dashboard");
 *   else if (isVenueOwner) navigate("/venue-dashboard");
 * };
 *
 * @example
 * // Protected route component
 * const { isAuthenticated, isLoading } = useAuth();
 *
 * if (isLoading) return <LoadingScreen />;
 * if (!isAuthenticated) return <Navigate to="/sign-in" />;
 *
 * return <Outlet />;
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error(
      "useAuth must be used within an AuthProvider. " +
        "Wrap your app with <AuthProvider> to use authentication features.",
    );
  }

  return context;
}

/**
 * Type guard to check if a user has member profile.
 *
 * @param userType - The user_type from user metadata
 * @returns True if user is a member
 *
 * @example
 * const { userType } = useAuth();
 * if (isMemberType(userType)) {
 * }
 */
export function isMemberType(userType: string | null): userType is "member" {
  return userType === "member";
}

/**
 * Type guard to check if a user has venue owner profile..
 *
 * @param userType - The user_type from user metadata
 * @returns True if user is a venue owner
 *
 * @example
 * const { userType } = useAuth();
 * if (isVenueOwnerType(userType)) {
 * }
 */
export function isVenueOwnerType(
  userType: string | null,
): userType is "venue_owner" {
  return userType === "venue_owner";
}
